import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface RmRepairSpare { id: number; description: string; quantity: number; unitCost: number; lineCost: number }
export interface RmRepairOrder {
  id: number;
  repairCode: string;
  complaintId: number;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  diagnosis: string | null;
  actionTaken: string | null;
  labourCost: number;
  sparesCost: number;
  totalCost: number;
  completedAt: string | null;
  createdAt: string;
  spares?: RmRepairSpare[];
}

export interface RmWarrantyClaim {
  id: number;
  claimCode: string;
  complaintId: number;
  status: 'raised' | 'approved' | 'rejected' | 'settled';
  claimAmount: number;
  settledAmount: number | null;
  remarks: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface RmComplaint {
  id: number;
  complaintCode: string;
  customerName: string;
  contact: string | null;
  product: string | null;
  serialNumber: string | null;
  description: string | null;
  channel: string;
  priority: string;
  status: 'open' | 'assigned' | 'in_repair' | 'resolved' | 'closed' | 'cancelled';
  warrantyStatus: 'in_warranty' | 'out_of_warranty' | 'unknown';
  assignedToType: string | null;
  assignedToId: number | null;
  resolvedAt: string | null;
  remarks: string | null;
  createdAt: string;
  repairOrders?: RmRepairOrder[];
  warrantyClaims?: RmWarrantyClaim[];
}

interface Paginated<T> { data: T[]; meta: PaginationMeta }

export const repairMaintenanceApi = {
  // Complaints
  async listComplaints(params: Record<string, unknown> = {}): Promise<Paginated<RmComplaint>> {
    const { data } = await apiClient.get<ApiSuccess<RmComplaint[]>>('/rm-complaints', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  getComplaint: async (id: number) => (await apiClient.get<ApiSuccess<RmComplaint>>(`/rm-complaints/${id}`)).data.data,
  createComplaint: async (body: Record<string, unknown>) => (await apiClient.post<ApiSuccess<RmComplaint>>('/rm-complaints', body)).data.data,
  assignComplaint: async (id: number, body: { assigneeType: string; assigneeId: number }) => (await apiClient.post<ApiSuccess<RmComplaint>>(`/rm-complaints/${id}/assign`, body)).data.data,
  closeComplaint: async (id: number, remarks?: string) => (await apiClient.post<ApiSuccess<RmComplaint>>(`/rm-complaints/${id}/close`, { remarks })).data.data,
  cancelComplaint: async (id: number, remarks?: string) => (await apiClient.post<ApiSuccess<RmComplaint>>(`/rm-complaints/${id}/cancel`, { remarks })).data.data,

  // Repair orders
  createRepair: async (body: { complaintId: number; diagnosis?: string }) => (await apiClient.post<ApiSuccess<RmRepairOrder>>('/rm-repair-orders', body)).data.data,
  getRepair: async (id: number) => (await apiClient.get<ApiSuccess<RmRepairOrder>>(`/rm-repair-orders/${id}`)).data.data,
  addSpare: async (id: number, body: { description: string; quantity: number; unitCost?: number }) => (await apiClient.post<ApiSuccess<RmRepairOrder>>(`/rm-repair-orders/${id}/spares`, body)).data.data,
  completeRepair: async (id: number, body: { actionTaken?: string; labourCost?: number }) => (await apiClient.post<ApiSuccess<RmRepairOrder>>(`/rm-repair-orders/${id}/complete`, body)).data.data,

  // Warranty claims
  createWarranty: async (body: { complaintId: number; claimAmount: number; remarks?: string }) => (await apiClient.post<ApiSuccess<RmWarrantyClaim>>('/rm-warranty-claims', body)).data.data,
  decideWarranty: async (id: number, body: { decision: string; settledAmount?: number; remarks?: string }) => (await apiClient.post<ApiSuccess<RmWarrantyClaim>>(`/rm-warranty-claims/${id}/decide`, body)).data.data,
};
